import React from 'react'
import ReactDOM from 'react-dom/client'
import { SSRDataProvider, type SSRData } from './lib/ssrData'
import { localeReady } from './i18n'
import { installChunkReloadHandlers, reloadForChunkLoadError } from './lib/chunkReload'
import { initConsentMode } from './lib/consent'
import { installBrowserErrorCapture, markHydration, rememberInitialPage } from './lib/browserErrors'
import { installBrowserLatencyCapture } from './lib/browserLatency'
import './style/index.css'

// En premier : une erreur au démarrage est justement celle qu'on ne voit jamais (journal des
// erreurs du site, cf. lib/browserErrors.ts)
installBrowserErrorCapture()
// Lenteurs vues par le visiteur : chargement, gels, clics lents, appels /api lents (lib/browserLatency.ts)
installBrowserLatencyCapture()
initConsentMode()
installChunkReloadHandlers()

const container = document.getElementById('root')!
// Le HTML du serveur tel que reçu (extensions et traduction comprises) : sert à décrire
// l'écart quand l'hydratation échoue (React #418)
rememberInitialPage(container)

/**
 * Prévient le garde-fou inline du SSR (`staticAssetGuard.ts`) que l'application a
 * démarré. Sans ce signal au bout de 15 s, il signale `app_not_started` : c'est la
 * seule trace d'un navigateur trop ancien pour nos modules (il les ignore sans erreur).
 */
function markBooted() {
  (window as Window & { __mfBooted?: boolean }).__mfBooted = true
}

async function bootstrap() {
  // Page maintenance SSR : HTML statique, pas besoin d'hydratation React
  // (evite le mismatch #418 entre le HTML inline SSR et le composant MaintenancePage)
  if ((window as any).__SSR_MAINTENANCE__) {
    // Laisser le HTML statique tel quel — aucune interactivite necessaire
    markBooted()
    return
  }

  await localeReady
  const { default: App } = await import('./App')

  if (container.innerHTML.trim()) {
    // Hydratation SSR : recuperer les donnees pre-fetchees injectees par le serveur
    const ssrData: SSRData = (window as any).__SSR_DATA__ || {}
    markHydration('pendant')
    ReactDOM.hydrateRoot(
      container,
      <SSRDataProvider data={ssrData}>
        <App />
      </SSRDataProvider>
    )
    markBooted()
    // Liberer la memoire apres hydratation
    delete (window as any).__SSR_DATA__

    // Nettoyage défensif d'un DOM ghost : sur certaines pages (notamment /), React
    // hydrate sans détecter de mismatch console mais laisse l'ancien <main> SSR à
    // côté du nouveau tree React → page rendue 2 fois visuellement. Tant que la
    // cause exacte du mismatch silencieux n'est pas identifiée, on supprime les
    // <main>/<footer> orphelins sans listeners React après l'hydratation.
    // MutationObserver : detecte quand React a fini d'ajouter ses elements et
    // dedupliques les <main>/<footer> en doublon (garde toujours le dernier =
    // tree React vivant).
    const cleanupGhosts = () => {
      const root = document.getElementById('root')
      if (!root) return
      const mains = root.querySelectorAll(':scope > main')
      const footers = root.querySelectorAll(':scope > footer')
      let removed = 0
      if (mains.length > 1) {
        for (let i = 0; i < mains.length - 1; i++) { mains[i].remove(); removed++ }
      }
      if (footers.length > 1) {
        for (let i = 0; i < footers.length - 1; i++) { footers[i].remove(); removed++ }
      }
      if (removed > 0) console.warn(`[hydrate cleanup] Removed ${removed} ghost element(s)`)
    }
    // Nettoyage au tick suivant + 200ms + observer pour les ajouts ultérieurs
    Promise.resolve().then(cleanupGhosts)
    setTimeout(cleanupGhosts, 200)
    setTimeout(cleanupGhosts, 1000)
    if (typeof MutationObserver !== 'undefined') {
      const obs = new MutationObserver(() => cleanupGhosts())
      obs.observe(container, { childList: true })
      setTimeout(() => obs.disconnect(), 5000)
    }
  } else {
    const root = ReactDOM.createRoot(container)
    root.render(<App />)
    // Rendu sans hydratation : une erreur ultérieure n'est pas « avant l'hydratation »
    markHydration('apres')
    markBooted()
  }
}

bootstrap().catch((error) => {
  if (reloadForChunkLoadError(error)) return
  throw error
})
